// T-411 — Guida d'uso staff (FAQ) consultabile in-app. Pagina STATICA: nessun
// dato, nessuna rete → funziona sempre, anche offline. Raggiunta dalla tile
// "Guida" (registry.ts, key 'guida', route '/guida'). Testi it-first, allineati
// al manuale `Materiali TrackEAT/Manuale-Staff-TrackEAT.md/.docx`.
//
// T-453: i percorsi "HACCP → X" sotto erano stale dalla rimozione della tab
// HACCP (T-013, dashboard unica) — le funzioni sono raggiungibili dalla
// scheda Oggi (riquadri Stato HACCP / Azioni veloci), non da una tab HACCP
// che non esiste più. Corretto in coppia col refresh del manuale staff.
import {
  IonAccordion,
  IonAccordionGroup,
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { palette } from '../components/design.js';

type Operazione = { icon: string; title: string; body: string };
type Faq = { q: string; a: string };

const OPERAZIONI: Operazione[] = [
  {
    icon: '🌡️',
    title: 'Registrare le temperature',
    body: 'Oggi → riquadro Temperature. Una volta al giorno controlli ogni frigo/congelatore: se la temperatura è a posto tocca "Conforme", altrimenti segnala il problema. Resta registrato con data, ora e il tuo nome.',
  },
  {
    icon: '🧽',
    title: 'Segnare le pulizie',
    body: 'Oggi → riquadro Pulizie. Trovi l\'elenco delle pulizie da fare: tocca quella completata per spuntarla. Le pulizie settimanali/mensili compaiono da sole nel giorno previsto.',
  },
  {
    icon: '🏷️',
    title: 'Stampare un\'etichetta di preparazione',
    body: 'Oggi → riquadro Etichetta. Scegli la preparazione (i tuoi preferiti sono in "Stampa rapida" in alto), controlla scadenza e copie, e stampa. Sull\'etichetta c\'è un QR: chi lo inquadra vede ingredienti, allergeni e tracciabilità del lotto.',
  },
  {
    icon: '❄️',
    title: 'Etichetta materia prima (decongelato / aperto)',
    body: 'Oggi → riquadro Etichetta → 📦 Materia prima. Serve quando scongeli o apri una confezione: scegli l\'alimento e il lotto, imposta le ore/giorni di validità e stampa. L\'app registra anche lo scarico dal magazzino.',
  },
  {
    icon: '📥',
    title: 'Caricare la merce (bolla / fattura)',
    body: 'Documenti → Carica (foto, PDF o galleria). L\'app legge il documento e ti mostra le righe da confermare. Controlla e conferma: la merce entra in magazzino con lotto e scadenza. Vedi le domande sotto per le righe "in rosso".',
  },
  {
    icon: '📤',
    title: 'Scaricare la merce (consumo / scarto)',
    body: 'Magazzino → alimento → dettaglio lotto. Indichi quanto esce e il motivo (consumo o scarto). La giacenza si aggiorna. Anche stampare un\'etichetta materia prima scarica in automatico.',
  },
];

const FAQ: Faq[] = [
  {
    q: 'Serve internet per lavorare?',
    a: 'Dopo l’accesso e la prima sincronizzazione puoi registrare temperature, pulizie e movimenti di magazzino e stampare etichette con la stampante collegata anche senza internet. I dati si allineano quando torna la rete. Lettura automatica dei DDT, esportazione PDF e registri di tarature, infestanti, manutenzioni, non conformità e trasporti richiedono una connessione, come indicato nelle rispettive schermate.',
  },
  {
    q: 'Ho sbagliato una registrazione, come faccio?',
    a: 'Non si cancella nulla (serve per l\'ASL), ma si corregge: rifai l\'operazione col dato giusto, o usa l\'annullamento previsto (es. "Annulla" nello storico etichette). Il registro resta pulito e tracciabile.',
  },
  {
    q: 'Mi sono dimenticato di registrare ieri. Posso farlo dopo?',
    a: 'Sì. In Temperature e Pulizie c\'è la sezione "Giorni scorsi": recuperi le voci mancanti e l\'app ti chiede a che ora le hai davvero fatte (niente orari inventati).',
  },
  {
    q: 'Quando carico un documento alcune righe sono in rosso. Perché?',
    a: 'Sono le righe da controllare: prodotto nuovo (mai visto), lotto poco chiaro o unità strana. Sistemale (scegli il prodotto giusto, scrivi il lotto, correggi l\'unità) e il rosso sparisce. Finché c\'è rosso il tasto Conferma resta bloccato: è la protezione che evita errori in magazzino.',
  },
  {
    q: 'Compare "prodotto nuovo" ma esiste già. Che faccio?',
    a: 'Non crearne uno nuovo: sulla riga scegli "aggancia a un prodotto esistente" e selezionalo. Così eviti i doppioni. La prossima volta l\'app lo riconosce da sola.',
  },
  {
    q: 'Ho caricato male un documento. Posso correggerlo?',
    a: 'Sì: apri il documento e tocca "Correggi importazione". Si riapre la schermata già compilata (senza rifare la foto): sistemi prodotto/lotto/unità e salvi. Il vecchio carico viene sostituito, senza doppioni.',
  },
  {
    q: 'Il lotto non compare quando stampo o scarico.',
    a: 'Di solito perché quel lotto è a zero (già consumato). Ricarica la merce dal documento, oppure usa la rettifica per allineare la giacenza al conteggio reale.',
  },
  {
    q: 'Ho scaricato per sbaglio, come rimetto la quantità?',
    a: 'Dal dettaglio del lotto usa "Rettifica quantità" e imposta il numero reale (funziona in aumento e in calo). Se non vedi l\'opzione, chiedi a chi ha i permessi (titolare/consulente).',
  },
  {
    q: 'La stampante non stampa o stampa caratteri strani.',
    a: 'Accoppia prima la stampante nelle impostazioni Bluetooth del telefono, poi selezionala dai "dispositivi accoppiati" nell\'app. Usa il tasto Test/Calibra per verificarla.',
  },
  {
    q: 'Abbiamo più telefoni/tablet: i dati si allineano?',
    a: 'Sì. Le registrazioni si allineano automaticamente quando i dispositivi sono connessi. Con rete lenta o assente gli altri dispositivi le vedranno al termine della sincronizzazione.',
  },
  {
    q: 'Cosa vede l\'ispettore ASL?',
    a: 'TrackEAT genera i registri A4 firmati (temperature, pulizie, tracciabilità, ricevimento merci…) con data e ora reali. Registrare con costanza = controllo sempre in regola.',
  },
];

export function GuidePage(): JSX.Element {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Guida</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: 16 }}>
          {/* Intro */}
          <div
            style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 12,
              padding: '14px 16px',
              color: '#1e3a8a',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <b>TrackEAT tiene in ordine la sicurezza alimentare (HACCP) e il magazzino del locale.</b> Registri
            temperature, pulizie, etichette, carichi e scarichi; l&apos;app conserva tutto e produce i registri firmati
            per l&apos;ASL. <b>Le registrazioni quotidiane funzionano anche senza internet</b> dopo l&apos;accesso
            e la prima sincronizzazione. I dati si allineano quando torna la rete; le funzioni che richiedono
            una connessione sono indicate nelle schermate e nelle risposte qui sotto.
          </div>

          {/* Operazioni di ogni giorno */}
          <h2 style={{ fontSize: 17, color: palette.text, margin: '22px 0 10px' }}>Le operazioni di ogni giorno</h2>
          {OPERAZIONI.map((op) => (
            <div
              key={op.title}
              style={{
                background: palette.card,
                border: `1px solid ${palette.border}`,
                borderLeft: `3px solid ${palette.accent}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15, color: palette.text, marginBottom: 4 }}>
                <span style={{ marginRight: 8 }}>{op.icon}</span>
                {op.title}
              </div>
              <div style={{ fontSize: 13.5, color: palette.textMuted, lineHeight: 1.5 }}>{op.body}</div>
            </div>
          ))}

          {/* FAQ */}
          <h2 style={{ fontSize: 17, color: palette.text, margin: '24px 0 8px' }}>Domande frequenti</h2>
          <p style={{ fontSize: 13, color: palette.textFaint, margin: '0 0 10px' }}>
            Tocca una domanda per vedere la risposta.
          </p>
          <IonAccordionGroup>
            {FAQ.map((f, i) => (
              <IonAccordion key={i} value={`faq-${i}`}>
                <IonItem slot="header">
                  <IonLabel class="ion-text-wrap" style={{ fontWeight: 600 }}>
                    {f.q}
                  </IonLabel>
                </IonItem>
                <div slot="content" style={{ padding: '10px 16px', fontSize: 13.5, color: palette.textMuted, lineHeight: 1.55 }}>
                  {f.a}
                </div>
              </IonAccordion>
            ))}
          </IonAccordionGroup>

          {/* Buone pratiche */}
          <div
            style={{
              background: palette.successSoft,
              border: '1px solid #bbf7d0',
              borderRadius: 10,
              padding: '12px 14px',
              color: palette.success,
              fontSize: 13.5,
              lineHeight: 1.5,
              margin: '20px 0 8px',
            }}
          >
            <b>Buone pratiche.</b> Registra nel momento in cui fai l&apos;operazione · controlla scadenza e lotto
            sull&apos;etichetta prima di stampare · conferma sempre le righe rosse quando carichi un documento · quando
            l&apos;app propone «aggancia a esistente», usalo (niente doppioni).
          </div>

          <div style={{ height: 24 }} />
        </div>
      </IonContent>
    </IonPage>
  );
}
